import type { paths } from "@/api/types";
import { API_ORIGIN } from "@/shared/config";

import type {
  BodyOf,
  CursorPath,
  GetPath,
  Page,
  PatchPath,
  PathArgs,
  PostPath,
  QueryOf,
  Result,
} from "./envelope";
import { ApiError, NetworkError } from "./error";
import { clearToken, getToken } from "./token";

/**
 * Единственная точка выхода в сеть.
 *
 * Базовый URL — origin или пустая строка. Префикс `/api/v1` сюда НЕ добавляется:
 * он уже входит в ключи `paths` сгенерированного контракта (блока `servers` в
 * openapi нет), и вторая копия дала бы `/api/v1/api/v1/admin/dashboard`.
 */

/** Публичная ручка ровно одна: пока токена нет, прикладывать нечего. */
const PUBLIC_PATHS = new Set<string>(["/api/v1/admin/auth/login", "/api/v1/ping"]);

type Method = "GET" | "POST" | "PATCH";

interface RequestOptions {
  params?: Record<string, string | number> | undefined;
  query?: Record<string, unknown> | undefined;
  body?: unknown;
  signal?: AbortSignal | undefined;
}

/**
 * Реакция на 401. Ставится слоем app, чтобы клиент не зависел от роутера.
 *
 * В мини-аппе на этом месте молчаливый релогин по `initData`; здесь его быть
 * не может — вход требует пароля и кода второго фактора. Поэтому 401 означает
 * ровно одно: сессия кончилась, надо показать форму входа.
 */
type SessionExpiredFn = () => void;
let onSessionExpired: SessionExpiredFn | undefined;

export function setSessionExpiredHandler(fn: SessionExpiredFn): void {
  onSessionExpired = fn;
}

export function fillPath(template: string, params: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`В пути ${template} не передан параметр ${name}`);
    }
    return encodeURIComponent(String(value));
  });
}

function appendParam(search: URLSearchParams, key: string, value: unknown): void {
  // null/undefined не отправляем: бэкенд трактует присутствие параметра иначе,
  // чем его отсутствие (например, ?status= сузил бы выборку до пустой).
  if (value === undefined || value === null || value === "") return;
  if (typeof value === "string") {
    search.append(key, value);
  } else if (typeof value === "number" || typeof value === "boolean") {
    search.append(key, String(value));
  } else {
    // Объект в query — всегда ошибка вызова: в контракте таких параметров нет.
    // Молчаливый `[object Object]` в URL искать потом дороже.
    throw new Error(`Параметр ${key} не примитив и не может уйти в query`);
  }
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Списки уходят повторяющимся ключом (?status=pending&status=approved) —
    // так их принимает FastAPI. В мини-аппе таких параметров не было, и
    // перенесённый оттуда клиент на массиве бросал исключение: фильтр по
    // статусу заявок падал вместо запроса.
    if (Array.isArray(value)) {
      for (const item of value) appendParam(search, key, item);
      continue;
    }
    appendParam(search, key, value);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

async function request<T>(method: Method, template: string, options: RequestOptions): Promise<T> {
  const url = API_ORIGIN + fillPath(template, options.params) + buildQuery(options.query);

  const headers = new Headers();
  // FormData Content-Type НЕ ставим руками: браузер должен дописать к нему
  // boundary, а заданный вручную заголовок он не трогает — сервер получил бы
  // тело, которое нечем разобрать.
  const multipart = options.body instanceof FormData;
  if (options.body !== undefined && !multipart) {
    headers.set("Content-Type", "application/json");
  }
  if (!PUBLIC_PATHS.has(template)) {
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const init: RequestInit = {
    method,
    headers,
    // credentials намеренно не включаем: в dev бэкенд отдаёт
    // `allow_origins: ["*"]` вместе с `allow_credentials: true`, и браузер
    // отвергает такой ответ на запрос с credentials. Токен ходит заголовком.
    ...(options.body !== undefined
      ? { body: multipart ? (options.body as FormData) : JSON.stringify(options.body) }
      : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    if (options.signal?.aborted) throw cause;
    throw new NetworkError(cause);
  }

  const requestId = response.headers.get("X-Request-ID") ?? undefined;

  if (response.ok) {
    return (await response.json()) as T;
  }

  throw await toApiError(response, requestId);
}

async function toApiError(response: Response, requestId: string | undefined): Promise<ApiError> {
  let code = "UNKNOWN_ERROR";
  let message = `HTTP ${String(response.status)}`;
  let details: Record<string, unknown> = {};

  try {
    const payload: unknown = await response.json();
    if (payload && typeof payload === "object" && "error" in payload) {
      const body = (payload as { error: { code?: string; message?: string; details?: unknown } }).error;
      code = body.code ?? code;
      message = body.message ?? message;
      if (body.details && typeof body.details === "object") {
        details = body.details as Record<string, unknown>;
      }
    }
  } catch {
    // Тело не разобралось — так выглядит 502 от nginx с HTML внутри.
    // Ошибка всё равно должна дойти до UI в понятном виде.
  }

  return new ApiError({ status: response.status, code, message, details, requestId });
}

/**
 * 401 — конец сессии. Токен стирается здесь, а не в обработчике: иначе каждый
 * повисший в фоне запрос успел бы отправиться со своим протухшим токеном.
 * Ошибка при этом пробрасывается дальше — вызывающий код должен знать, что
 * запрос не выполнен.
 */
async function withSession<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof ApiError && error.code === "AUTH_ERROR") {
      clearToken();
      onSessionExpired?.();
    }
    throw error;
  }
}

export function apiGet<K extends GetPath>(
  path: K,
  options: {
    params?: PathArgs<K>;
    query?: QueryOf<paths[K]["get"]>;
    signal?: AbortSignal;
  } = {},
): Promise<Result<paths[K]["get"]>> {
  return withSession(async () => {
    const body = await request<{ data: unknown }>("GET", path, options);
    return body.data as Result<paths[K]["get"]>;
  });
}

/**
 * Курсорная страница. Конверт снимается не до `data`, а до `{items, meta}`:
 * без `meta` нечем остановить `useInfiniteQuery`.
 */
export function apiGetPage<K extends CursorPath & GetPath>(
  path: K,
  options: {
    params?: PathArgs<K>;
    query?: QueryOf<paths[K]["get"]>;
    signal?: AbortSignal;
  } = {},
): Promise<Page<paths[K]["get"]>> {
  return withSession(async () => {
    const body = await request<{ data: unknown; meta?: unknown }>("GET", path, options);
    return {
      items: body.data as Page<paths[K]["get"]>["items"],
      meta: body.meta as Page<paths[K]["get"]>["meta"],
    };
  });
}

/**
 * Тело у POST админки не всегда есть (`/accruals/roll` — кнопка без формы),
 * поэтому опции целиком необязательны. `Idempotency-Key` здесь не нужен: у
 * админских действий нет автоповтора, а повторное подтверждение заявки
 * бэкенд и так отвергает конфликтом статуса.
 */
interface PostOptions<K extends PostPath> {
  params?: PathArgs<K>;
  body?: BodyOf<paths[K]["post"]>;
  signal?: AbortSignal;
}

export function apiPost<K extends PostPath>(
  path: K,
  options: PostOptions<K> = {},
): Promise<Result<paths[K]["post"]>> {
  return withSession(async () => {
    const body = await request<{ data: unknown }>("POST", path, options);
    return body.data as Result<paths[K]["post"]>;
  });
}

/**
 * Загрузка файла. Отдельно от `apiPost`, потому что тело у неё не из контракта:
 * в сгенерированных типах multipart-схема описана как объект с полем `file`
 * типа `string`, и подставить туда `File` не даст компилятор.
 */
export function apiUpload<T>(
  path: string,
  options: { params?: Record<string, string | number>; file: File; signal?: AbortSignal },
): Promise<T> {
  const form = new FormData();
  form.append("file", options.file);

  return withSession(async () => {
    const body = await request<{ data: unknown }>("POST", path, {
      ...(options.params ? { params: options.params } : {}),
      body: form,
      ...(options.signal ? { signal: options.signal } : {}),
    });
    return body.data as T;
  });
}

export function apiPatch<K extends PatchPath>(
  path: K,
  options: { params?: PathArgs<K>; body?: BodyOf<paths[K]["patch"]>; signal?: AbortSignal } = {},
): Promise<Result<paths[K]["patch"]>> {
  return withSession(async () => {
    const body = await request<{ data: unknown }>("PATCH", path, options);
    return body.data as Result<paths[K]["patch"]>;
  });
}
